"use client";

import dynamic from "next/dynamic";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMobileViewPreference } from "@/hooks/use-mobile-view-preference";
import { useTodayYmd } from "@/hooks/use-today-ymd";
import { useNowTick } from "@/hooks/use-now-tick";
import { useOrderHold } from "@/hooks/use-order-hold";
import type { GameTime } from "@/lib/game-date";
import { WhalesGateProvider } from "@/components/odds/whales-gate";
import { useCatalog, useLiveStatus, usePropsMany, useRace } from "@/hooks/use-odds-live";
import { useFirstPaintGate } from "@/hooks/use-first-paint-gate";
import { getFantasyLiveClient } from "@/lib/odds-screen/live/client";
import { ActiveFilterChips } from "@/components/optimizer/active-filter-chips";
import { useOddsFormatPreference } from "@/components/optimizer/use-odds-format-preference";
import { useStickyOptimizerFilters } from "@/components/optimizer/use-sticky-optimizer-filters";
import { savePricingModel } from "@/lib/actions/pricing-model";
import { changedPricingModel } from "@/lib/pricing-model";
import {
  filterChanges,
  getOptimizerDismissalMetadata,
  getOptimizerDismissalTracking,
  propToSlipData,
  type LiveOptimizerPageProps,
} from "@/components/optimizer/optimizer-page-client";
import { useOptionalDismissedBets } from "@/contexts/dismissed-bets-context";
import { getOptimizerFingerprint } from "@/lib/dismissal-fingerprints";
import {
  buildClearedFilters,
  canonicalizeSportsbookPlatformState,
  sanitizeFantasyPropsQueryState,
  pageSearch,
  shouldShowActiveFilterChips,
  statesAreEqual,
  type FilterChangeOptions,
  type FilterChangeSource,
  type OptimizerQueryState,
  type OptimizerStickyFilterState,
  type SortDirection,
} from "@/components/optimizer/query-state";
import { FANTASY_PROPS_BOOKS } from "@/components/optimizer/props-books";
import { getSportsbookSurfacePolicy, SPORTSBOOK_SURFACE } from "@/lib/sportsbook-preferences";
import { OPTIMIZER_SURFACE_CONFIGS, optimizerSurfaceTitle } from "@/lib/optimizer-surface-config";
import {
  deleteWeightPreset,
  listUserWeightPresets,
  saveWeightPreset,
  setDefaultWeightPreset,
  type UserWeightPreset,
} from "@/lib/actions/weight-presets";
import { SAVED_FILTER_PAGE } from "@/lib/saved-filter-pages";
import type { BookWeights } from "@/lib/weights-config";
import { NO_ROW_SOURCE, UNGRADED, catalogGrading, createFold, createPeerBooks, displayRowsFor, foldRows, pricedChoices, pricingWeights, standardOf, whalesPriceOn } from "@/lib/fantasy-props/rows";
import { evaluateSlip, legFromRow, legProbability, listSlipTargets, resolveSlipTarget, rowIdOf, slipLock, slipMinLegs, slipPrice, slipsStayInOneLeague, withCurrent, type SlipLeg, type SlipTarget } from "@/lib/fantasy-props/slip";
import { candidatesFrom, enteredRowIds, generateBestSlips, type GeneratedSlip } from "@/lib/fantasy-props/best-slips";
import { blockingPill, leagueLabel, livePill } from "@/components/odds-screen/live/view-model";
import { BlockingPanel, BoardStatePanel, ConnectionBanner } from "@/components/odds-screen/live/board-states";
import { EmptyState } from "@/components/ui/empty-state";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { StatePanel } from "@/components/ui/state-panel";
import { BetLinkNudge } from "@/components/onboarding/BetLinkNudge";
import { useToolContentBoundary, useToolRootHydration } from "@/components/performance/tool-load-observer";
import { liveContentOutcome, liveContentReceipt } from "@/lib/performance/tool-load-timing";
import { MobileOptimizerCards } from "@/components/optimizer/mobile/mobile-optimizer-cards";
import {
  getOddsHistorySelectionIdentity,
  type OddsHistoryModalParams,
  type OpenOddsHistory,
} from "@/components/odds/odds-history-selection";
import { OddsHistoryModal } from "@/components/odds/lazy-odds-history-modal";
import type { DisplayPlayerProp, PlatformType } from "@/components/optimizer/utils-v2";
import type { SportsbookId } from "@/lib/sportsbook-registry";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { phCapture, trackFilterChanged, trackToolRowExpanded } from "@/utils/analytics";
import { FantasyBar, type SlipsBadge } from "./fantasy-bar";
import { useListedBooks } from "./book-picker";
import { BookStrip } from "./book-strip";
import type { RaceSlot } from "./race-panel";
import { FantasyBoard, HIT_COLUMN, type BoardSlip } from "./fantasy-board";
import { currentRace, raceLegBoosts, raceWithWeights } from "@/lib/fantasy-props/race";
import { FantasySlipPanel, type SlipView } from "./slip-panel";
import { usePick6Quote } from "./use-pick6-quote";
import { useUnderdogQuote } from "./use-underdog-quote";
import { slipPricing } from "./slip-pricing";
import { useFantasySlip } from "./use-slip";
import { useEnteredSlips } from "./use-entered-slips";
import { FantasyFiltersSheet } from "./filters-sheet";
import { getSlipBreakevens, isBoostableSlipPlatform } from "@/components/optimizer/props-slip-breakevens";
import { activeFilterCount, appNeedOf, bookColumns, columnBooks, countedLeagues, timeChoices, leagueScope, postingLeagues, propsLeagues, rowLines, showsWhales, shownStateOf, slipNeed } from "./model";
import { useRememberedLeagues } from "./use-remembered-leagues";
import { useHeldView } from "./use-held-view";
import { renderNothingIfChunkFails } from "@/lib/optional-chunk";
import { useChatPageLeague, useChatShell } from "@/components/chat/chat-shell";
import { stagePlay } from "@/components/chat/stage-play";

const ShareModal = dynamic(
  () => renderNothingIfChunkFails(import("@/components/slip/share-modal").then((mod) => mod.ShareModal)),
  { ssr: false },
);

const NO_HIDDEN: ReadonlySet<string> = new Set();
const NO_ROWS: ReadonlySet<string> = new Set();
const NO_PRESETS: UserWeightPreset[] = [];
const NO_BOOKS: readonly PlatformType[] = [];
const NO_TARGETS: SlipTarget[] = [];
const NO_LEG_ROWS: ReadonlyMap<string, DisplayPlayerProp> = new Map();
const RACE_BOOK = "betr";

export function FantasyPropsPage({
  initialState,
  initialSort,
  initialUserId,
  stickyFilterScope,
  initialStickyHydration,
  allowedSportsbooks = FANTASY_PROPS_BOOKS,
  myBooks,
  defaultBookIds = NO_BOOKS,
  comparisonPriorityOrder,
  sportsbookSurface,
  sportsbookPreferencesConfigured = false,
  sportsbookPreferenceRuntime,
  comparisonOrderRuntime,
  comparisonColumns,
  initialUserPresets,
  initialSportsbookFromExplicitUrl = false,
  showWeightsFilter = true,
  tutorial,
  betLinkNudgeDismissed = false,
  initialLiveSession = null,
  initialMobileView = "cards",
  diamond,
}: LiveOptimizerPageProps) {
  useToolRootHydration("fantasy-props");
  useState(() => {
    if (initialLiveSession !== null) getFantasyLiveClient().prime(initialLiveSession);
    return null;
  });
  const isMobile = useIsMobile() === true;
  const analyticalBookIds = useMemo(
    () => getSportsbookSurfacePolicy(sportsbookSurface ?? SPORTSBOOK_SURFACE.OPTIMIZER_FANTASY_PROPS).analyticalBookIds,
    [sportsbookSurface],
  );

  const sanitize = useCallback((next: OptimizerQueryState, fallbackPlatform?: PlatformType): OptimizerQueryState => {
    const canonical = canonicalizeSportsbookPlatformState(next, { allowedSportsbooks, fallbackPlatform });
    return sanitizeFantasyPropsQueryState({ ...canonical, offset: 0 });
  }, [allowedSportsbooks]);

  const [state, setState] = useState<OptimizerQueryState>(() =>
    sanitize({ ...initialState, sort: initialSort.column, order: initialSort.direction }),
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const hasUserInteractedRef = useRef(false);

  const updateUrl = useCallback((next: OptimizerQueryState) => {
    if (typeof window === "undefined") return;
    const params = pageSearch(next, window.location.search);
    window.history.replaceState(null, "", `${window.location.pathname}${params ? `?${params}` : ""}`);
  }, []);

  const apply = useCallback((next: OptimizerQueryState) => {
    const clean = sanitize(next, stateRef.current.platform);
    setState((previous) => (statesAreEqual(previous, clean) && previous.preset === clean.preset ? previous : clean));
    updateUrl(clean);
  }, [sanitize, updateUrl]);

  const handleChange = useCallback((partial: Partial<OptimizerQueryState>, options?: FilterChangeOptions) => {
    hasUserInteractedRef.current = true;
    const merged: OptimizerQueryState = { ...stateRef.current, ...partial };
    if (partial.platform && !partial.sportsbook) merged.sportsbook = partial.platform;
    const model = changedPricingModel(stateRef.current, merged);
    if (model !== null && initialUserId) savePricingModel(model).catch(() => {});
    const next = sanitize(merged, stateRef.current.platform);
    if (statesAreEqual(stateRef.current, next) && stateRef.current.preset === next.preset) return;
    for (const change of filterChanges(partial, next)) {
      trackFilterChanged({
        ...change,
        page: "optimizer",
        source: options?.source ?? "bar",
        ...(change.filter_name === "player_search" ? { debounce_ms: 0 } : {}),
      });
    }
    apply(next);
  }, [apply, initialUserId, sanitize]);

  const sanitizeStickyState = useCallback((sticky: OptimizerStickyFilterState): OptimizerStickyFilterState => {
    const sportsbook = sportsbookPreferencesConfigured
      ? ""
      : allowedSportsbooks.includes(sticky.sportsbook as PlatformType) ? sticky.sportsbook : "";
    return { ...sticky, sportsbook, minEV: 0 };
  }, [allowedSportsbooks, sportsbookPreferencesConfigured]);

  const { hydrationComplete, forgetSavedFilters } = useStickyOptimizerFilters({
    initialUserId,
    scope: stickyFilterScope,
    state,
    initialHydration: initialStickyHydration,
    applyHydratedState: apply,
    hasUserInteractedRef,
    sanitizeStickyState,
  });

  const transientBookRef = useRef(initialSportsbookFromExplicitUrl);
  useEffect(() => {
    if (!hydrationComplete || !sportsbookPreferencesConfigured || !sportsbookPreferenceRuntime) return;
    if (transientBookRef.current) {
      if (allowedSportsbooks.includes(stateRef.current.sportsbook as PlatformType)) return;
      transientBookRef.current = false;
    }
    const { effectiveBookId } = sportsbookPreferenceRuntime;
    if (stateRef.current.sportsbook === effectiveBookId) return;
    apply({ ...stateRef.current, sportsbook: effectiveBookId, platform: effectiveBookId });
  }, [allowedSportsbooks, apply, hydrationComplete, sportsbookPreferenceRuntime, sportsbookPreferencesConfigured]);

  const handleBook = useCallback((bookId: PlatformType, source: FilterChangeSource = "bar") => {
    if (!allowedSportsbooks.includes(bookId)) return;
    handleChange({ sportsbook: bookId, platform: bookId }, { source });
    if (!sportsbookPreferencesConfigured || !sportsbookPreferenceRuntime) return;
    transientBookRef.current = false;
    void sportsbookPreferenceRuntime.saveSelection(bookId).then((saved) => {
      if (saved) phCapture("optimizer_sportsbook_last_used_saved", { surface: sportsbookSurface ?? "optimizer", sportsbook: bookId });
    });
  }, [allowedSportsbooks, handleChange, sportsbookPreferenceRuntime, sportsbookPreferencesConfigured, sportsbookSurface]);

  const { shown, place, setHereOnly } = useBookAvailability();
  const book = shown(state.platform, defaultBookIds);
  const { listed: listedBooks } = useListedBooks(state.platform, allowedSportsbooks, myBooks);
  const shownState = useMemo(() => shownStateOf(state, book), [book, state]);
  useEffect(() => {
    document.title = `${optimizerSurfaceTitle(OPTIMIZER_SURFACE_CONFIGS["fantasy-props"], book)} | Upside`;
  }, [book]);
  const selectedLeagues = useMemo(() => state.leagues.map((id) => id.toLowerCase()), [state.leagues]);
  const liveClient = getFantasyLiveClient();
  const catalog = useCatalog(liveClient);
  const status = useLiveStatus(liveClient);
  const { view: mobileView, setView: setMobileView } = useMobileViewPreference(initialMobileView);
  const catalogLeagues = useMemo(() => propsLeagues(catalog.value, selectedLeagues), [catalog.value, selectedLeagues]);
  const catalogReady = catalog.value !== null;
  const gradingOf = useMemo(() => catalogGrading(catalog.value?.books), [catalog.value?.books]);
  const grading = book === null ? UNGRADED : gradingOf(book);
  const remembered = useRememberedLeagues("fantasy-props", catalogReady ? postingLeagues(catalogLeagues) : null);
  const scope = useMemo(() => leagueScope(selectedLeagues, catalogReady ? catalogLeagues : null, remembered), [selectedLeagues, catalogReady, catalogLeagues, remembered]);
  useChatPageLeague(scope.single);
  useEffect(() => {
    if (catalogLeagues.length === 0 || scope.known) return;
    apply({ ...stateRef.current, leagues: scope.knownSelected.map((id) => id.toUpperCase()) });
  }, [apply, scope, catalogLeagues]);

  const subscribedLeagues = scope.subscribed;
  const live = usePropsMany(subscribedLeagues, book);
  const raceState = useRace(diamond ? RACE_BOOK : null);
  const searchParams = useSearchParams();
  const [raceParam, setRaceParam] = useState(() => searchParams?.get("race") ?? null);
  const race = useMemo(() => currentRace(raceState, raceParam), [raceState, raceParam]);
  const alertClosed = raceParam !== null && race?.id !== raceParam;
  const raceLegs = useMemo(() => (book === RACE_BOOK ? raceLegBoosts(race) : raceLegBoosts(null)), [book, race]);
  const [raceOpen, setRaceOpen] = useState(false);
  const openRace = useCallback(() => setRaceOpen(true), []);
  const closeRace = useCallback(() => {
    setRaceOpen(false);
    if (alertClosed) setRaceParam(null);
  }, [alertClosed]);
  const toggleRace = useCallback(() => (raceOpen ? closeRace() : openRace()), [raceOpen, closeRace, openRace]);
  const [isLive, setIsLive] = useState(true);
  const toggleLive = useCallback(() => setIsLive((value) => !value), []);
  const { view, heldAtMs } = useHeldView(live, isLive, `${book}|${subscribedLeagues.join(",")}`);
  const leagues = useMemo(() => countedLeagues(catalogLeagues, view, subscribedLeagues), [catalogLeagues, view, subscribedLeagues]);

  const dismissed = useOptionalDismissedBets();
  const hidden = dismissed?.dismissedBetFingerprints ?? NO_HIDDEN;
  const [userPresets, setUserPresets] = useState<UserWeightPreset[]>(initialUserPresets ?? NO_PRESETS);
  useEffect(() => {
    if (initialUserPresets != null) return;
    listUserWeightPresets(SAVED_FILTER_PAGE.OPTIMIZER).then(setUserPresets).catch(() => {});
  }, [initialUserPresets]);
  const weights = useMemo(() => pricingWeights(state, userPresets, diamond), [state, userPresets, diamond]);
  const weighted = weights !== null;
  const pricedRace = useMemo(() => (race === null ? null : raceWithWeights(race, weights)), [race, weights]);
  const raceView = useMemo((): RaceSlot["state"] | null => {
    if (!diamond) return { kind: "locked" };
    if (pricedRace !== null) return { kind: "live", race: pricedRace, closed: alertClosed };
    if (raceState?.phase === "live" || raceState?.phase === "resyncing") return { kind: "idle", closed: alertClosed };
    return null;
  }, [diamond, pricedRace, raceState, alertClosed]);
  const raceParamHandledRef = useRef(false);
  useEffect(() => {
    if (raceParam === null || raceParamHandledRef.current) return;
    if (diamond && (raceState === null || raceState.phase === "loading")) return;
    raceParamHandledRef.current = true;
    if (raceView !== null) openRace();
    if (!diamond) phCapture("race_gate_shown", { trigger: "deep_link" });
    phCapture("notification_opened", { kind: "race_plus_ev", race_id: raceParam, matched: !alertClosed, locked: !diamond });
  }, [raceParam, diamond, raceState, alertClosed, raceView, openRace]);

  const slip = useFantasySlip({ userId: initialUserId, book });
  const oneLeague = book !== null && slipsStayInOneLeague(book);
  const lock = useMemo(() => (book === null ? null : slipLock(slip.legs, book)), [slip.legs, book]);
  const minLegs = slipMinLegs(slip.legs);
  const slipLeague = lock?.league ?? scope.single;
  const legCount = slip.legs.length;
  const slipTargets = useMemo(() => (book === null ? NO_TARGETS : listSlipTargets(book, state.boost, minLegs)), [book, state.boost, minLegs]);
  const slipBook = slipTargets.length > 0;
  const panelTargets = useMemo(() => (book === null ? NO_TARGETS : listSlipTargets(book, state.boost, Math.max(minLegs, legCount), slipLeague)), [book, state.boost, minLegs, legCount, slipLeague]);
  const slipTargetId = slip.target ?? shownState.slipType;
  const slipTarget = useMemo(() => (book === null ? null : resolveSlipTarget(book, slipTargetId, state.boost, minLegs, slipLeague, legCount)), [book, slipTargetId, state.boost, minLegs, slipLeague, legCount]);
  const legRows = useMemo(
    () => (book === null ? NO_LEG_ROWS : displayRowsFor({ view, state: shownState, book, userPresets, rowIds: slip.legs.map((leg) => leg.rowId), gradingOf, diamond })),
    [view, shownState, book, userPresets, slip.legs, gradingOf, diamond],
  );
  const boardLoaded = view.pending === 0 && (view.phase !== "loading" || (catalogReady && scope.known));
  const legViews = useMemo(() => slip.legs.map((leg) => withCurrent(leg, legRows.get(leg.rowId), boardLoaded)), [slip.legs, legRows, boardLoaded]);
  const pick6 = usePick6Quote(book, legViews);
  const underdog = useUnderdogQuote(book, legViews);
  const listPrice = useMemo(() => slipPrice(legViews), [legViews]);
  const pricing = useMemo(() => slipPricing(grading, underdog, pick6, slipTarget, listPrice, legViews), [grading, underdog, pick6, slipTarget, listPrice, legViews]);
  const slipEvaluation = useMemo(
    () => (slipTarget === null || pricing.price === null ? null : evaluateSlip(legViews.map(legProbability), slipTarget, pricing.price, pricing.tiers)),
    [legViews, slipTarget, pricing],
  );
  const slipRoomPct = slipEvaluation !== null && legViews.length > 0 && slipEvaluation.remaining > 0 ? slipEvaluation.roomPct : null;
  useEffect(() => {
    if (!pricing.mismatch || slipTarget === null || pick6.status !== "priced") return;
    phCapture("fantasy_pick6_table_mismatch", { pick_group: pick6.quote.pickGroup, slip_type: slipTarget.id, league: lock?.league ?? null, ours: slipTarget.base, dk: pricing.tiers ?? null });
  }, [pricing.mismatch, pricing.tiers, slipTarget]);
  const nowMs = useNowTick();
  const [fold] = useState(createFold);
  const cardView = isMobile && mobileView === "cards";
  const boardState = useMemo(() => (!cardView && shownState.sort !== HIT_COLUMN ? { ...shownState, sort: HIT_COLUMN, order: "desc" as const } : shownState), [cardView, shownState]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const { rows, cue } = useOrderHold({
    fold,
    feed: { view, nowMs, gradingOf },
    settings: { state: boardState, book, userPresets, diamond },
    hides: { hidden },
    openId: expanded,
  });
  const gate = useFirstPaintGate(view, subscribedLeagues.length, rows.ids.length, book ?? undefined);
  const [peerBooksOf] = useState(createPeerBooks);
  const peerBooksKey = useMemo(() => peerBooksOf(view.rows).join(","), [peerBooksOf, view.rows]);
  const columns = useMemo(
    () => bookColumns(peerBooksKey.split(","), comparisonColumns, analyticalBookIds, book ?? undefined),
    [peerBooksKey, comparisonColumns, analyticalBookIds, book],
  );
  const shownBooks = useMemo(() => columnBooks(columns), [columns]);
  const whalesShown = showsWhales(columns);
  const needPct = useMemo(() => (book === null || grading.kind === "ungraded" ? null : slipNeed(book, shownState.slipType, state.boost, scope.single)), [book, grading, shownState.slipType, state.boost, scope.single]);
  const needOf = useMemo(() => appNeedOf(state, book, sanitize, scope.single, cardView, gradingOf), [book, sanitize, state, scope.single, cardView, gradingOf]);
  const boostable = book !== null && isBoostableSlipPlatform(book);

  const tickMs = useMemo(() => Date.now(), [view]);
  const pool = useMemo(
    () => (book === null ? NO_ROW_SOURCE : foldRows({ view, state: shownState, book, userPresets, hidden, purpose: "slips", nowMs, gradingOf, diamond })),
    [view, shownState, book, userPresets, hidden, nowMs, gradingOf, diamond],
  );
  const candidates = useMemo(() => (book === null ? [] : candidatesFrom(pool, view, tickMs, book, standardOf(grading))), [pool, view, tickMs, book, grading]);
  const { entered, add: addEntered, remove: removeEntered } = useEnteredSlips({ userId: initialUserId, book, nowMs: tickMs });
  const swapScope = `${book}:${selectedLeagues.join(",")}`;
  const [swapped, setSwapped] = useState<{ scope: string; ids: ReadonlySet<string> }>({ scope: swapScope, ids: NO_ROWS });
  const swappedIds = swapped.scope === swapScope ? swapped.ids : NO_ROWS;
  const [minEvPct, setMinEvPct] = useState(0);
  const bestSlips = useMemo(() => generateBestSlips({
    candidates,
    targets: slipTargets,
    unpriced: ((book === null ? null : getSlipBreakevens(book)) ?? []).filter((slipType) => !slipType.ladder).map((slipType) => slipType.label),
    minEvPct,
    excluded: new Set([...swappedIds, ...enteredRowIds(entered)]),
    oneLeague,
  }), [candidates, slipTargets, book, minEvPct, swappedIds, entered, oneLeague]);
  const [slipView, setSlipView] = useState<SlipView>("best");
  const hadLegsRef = useRef(false);
  useEffect(() => {
    const hasLegs = slip.legs.length > 0;
    if (hasLegs && !hadLegsRef.current) setSlipView("mine");
    hadLegsRef.current = hasLegs;
  }, [slip.legs.length]);
  const [preview, setPreview] = useState<ReadonlySet<string> | null>(null);

  const eventsRef = useRef(view.events);
  eventsRef.current = view.events;
  const chat = useChatShell();
  const chatShare = isLive && status === "live" && view.phase === "live" ? chat?.share : undefined;
  const shareInputs = useRef({ catalog: catalog.value, pricedAt: rows.pricedAt });
  shareInputs.current = { catalog: catalog.value, pricedAt: rows.pricedAt };
  const shareToChat = useMemo(
    () =>
      chatShare &&
      ((row: DisplayPlayerProp) => {
        const { catalog, pricedAt } = shareInputs.current;
        const priced = pricedAt(rowIdOf(row));
        chatShare(priced && stagePlay("fantasy", priced, catalog));
      }),
    [chatShare],
  );
  const boardSlip = useMemo<BoardSlip | null>(() => (book === null || !slipBook ? null : {
    has: (rowId) => (preview !== null ? preview.has(rowId) : slip.has(rowId)),
    toggle: (row) => {
      const event = row.event_id ? eventsRef.current.get(row.event_id) : undefined;
      slip.toggle(legFromRow(row, book, rowLines(row, event)), !isMobile);
      setSlipView("mine");
      phCapture("fantasy_slip_leg_toggled", { sportsbook: book, slip_type: slipTarget?.id ?? null, in_slip: !slip.has(legFromRow(row, book, rowLines(row, event)).rowId) });
    },
  }), [slipBook, slipTarget, slip, book, isMobile, preview]);
  const openSlips = useCallback((nextView: SlipView) => {
    setSlipView(nextView);
    slip.setOpen(true);
    if (nextView === "best") phCapture("fantasy_best_slips_opened", { sportsbook: book, best_ev_pct: bestSlips.best === null ? null : Math.round(bestSlips.best.evPct * 10) / 10 });
  }, [slip, book, bestSlips.best]);
  const useBestSlip = useCallback((generated: GeneratedSlip, target: SlipTarget) => {
    if (book === null) return;
    const legs: SlipLeg[] = [];
    for (const rowId of generated.rowIds) {
      const row = pool.rowAt(rowId);
      if (row === null) continue;
      legs.push(legFromRow(row, book, rowLines(row, row.event_id ? view.events.get(row.event_id) : undefined)));
    }
    slip.load(legs, target.id);
    setSlipView("mine");
    phCapture("fantasy_best_slip_used", { sportsbook: book, slip_type: target.id, ev_pct: Math.round(generated.evPct * 10) / 10, legs: legs.length });
  }, [pool, book, view.events, slip]);
  const enterBestSlip = useCallback((generated: GeneratedSlip, target: SlipTarget) => {
    addEntered({ id: generated.id, targetId: target.id, rowIds: generated.rowIds, firstStartMs: generated.firstStartMs });
    phCapture("fantasy_best_slip_entered", { sportsbook: book, slip_type: target.id, ev_pct: Math.round(generated.evPct * 10) / 10 });
  }, [addEntered, book]);
  const legsText = slipTarget === null ? `${legCount}` : `${legCount} of ${slipTarget.legs}`;
  const slipsBadge: SlipsBadge | null = legCount > 0
    ? { text: legsText, tone: "primary" }
    : bestSlips.best !== null ? { text: `+${bestSlips.best.evPct.toFixed(1)}%`, tone: "value" } : null;
  const readyTypes = bestSlips.targets.filter((entry) => entry.status === "ready").length;
  const cardsSlip = useMemo(
    () => (boardSlip === null ? undefined : { inSlip: (row: DisplayPlayerProp) => boardSlip.has(rowIdOf(row)), toggle: boardSlip.toggle }),
    [boardSlip],
  );
  const handleBoost = useCallback((value: number) => {
    if (value === stateRef.current.boost) return;
    phCapture("fantasy_boost_set", { sportsbook: book, boost_pct: value });
    handleChange({ boost: value }, { source: "bar" });
  }, [book, handleChange]);
  const todayYmd = useTodayYmd();
  const times = useMemo(() => timeChoices(view, todayYmd, nowMs), [view, todayYmd, nowMs]);
  const time = useMemo<GameTime>(() => ({ days: state.gameDays, within: state.startsWithin, live: false }), [state.gameDays, state.startsWithin]);

  const pageViewedRef = useRef(false);
  useEffect(() => {
    if (pageViewedRef.current || !hydrationComplete) return;
    pageViewedRef.current = true;
    const current = stateRef.current;
    phCapture("optimizer_page_viewed", {
      sport: current.leagues[0]?.toLowerCase() ?? null,
      league: current.leagues[0]?.toUpperCase() ?? null,
      books_selected: current.sportsbook ? 1 : 0,
      result_count: rows.ids.length,
      has_filters_applied: current.statTypes.length > 0 || current.matchups.length > 0 || current.playerSearch !== "",
      table_variant: "props",
    });
  }, [rows.ids.length, hydrationComplete]);

  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const handleToggle = useCallback((key: string) => {
    if (expandedRef.current !== key) trackToolRowExpanded({ tool: "props", row_kind: "prop" });
    setExpanded((previous) => (previous === key ? null : key));
  }, []);

  const [shareRow, setShareRow] = useState<DisplayPlayerProp | null>(null);
  const shareSlipData = useMemo(
    () => (shareRow ? propToSlipData(shareRow, false, comparisonPriorityOrder) : null),
    [shareRow, comparisonPriorityOrder],
  );
  const handleHide = useCallback((row: DisplayPlayerProp) => {
    const fingerprint = getOptimizerFingerprint(row);
    if (!fingerprint || !dismissed) return;
    if (expandedRef.current === rowIdOf(row)) setExpanded(null);
    dismissed.hideBet(fingerprint, {
      meta: getOptimizerDismissalMetadata(row, "props"),
      tracking: getOptimizerDismissalTracking(row, "props"),
      toastTitle: "Bet hidden",
      toastDescription: "We’ll hide matching bets across tools. Use Undo or Manage Hidden to restore it.",
    });
  }, [dismissed]);

  const hideLegs = useCallback((rowIds: readonly string[]) => {
    if (!dismissed) return;
    const hideable = rowIds.flatMap((rowId) => {
      const row = legRows.get(rowId);
      const fingerprint = row ? getOptimizerFingerprint(row) : null;
      if (!row || !fingerprint) return [];
      return [{
        rowId,
        entry: {
          fingerprint,
          meta: getOptimizerDismissalMetadata(row, "props"),
          tracking: { ...getOptimizerDismissalTracking(row, "props"), source: "optimizer_props_slip" },
        },
      }];
    });
    if (hideable.length === 0) return;
    if (hideable.some(({ rowId }) => rowId === expandedRef.current)) setExpanded(null);
    const entries = hideable.map(({ entry }) => entry);
    const undoRemove = slip.remove(...hideable.map(({ rowId }) => rowId));
    const many = entries.length > 1;
    dismissed.hideBets(entries, {
      toastTitle: many ? `${entries.length} picks hidden` : "Pick hidden",
      toastDescription: `We'll hide matching bets across tools. Use Undo or Manage Hidden to restore ${many ? "them" : "it"}.`,
      onUndo: undoRemove,
    });
  }, [dismissed, legRows, slip]);

  const [historyModal, setHistoryModal] = useState<OddsHistoryModalParams | null>(null);
  const openHistory = useCallback<OpenOddsHistory>((params) => setHistoryModal(params), []);

  const { format, setFormat } = useOddsFormatPreference();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const handleSavePreset = useCallback(async (name: string, weights: BookWeights, requiredBooks: string[]) => {
    const result = await saveWeightPreset(SAVED_FILTER_PAGE.OPTIMIZER, name, weights, requiredBooks);
    if (!result.success) throw new Error(result.error || "Failed to save preset");
    setUserPresets(await listUserWeightPresets(SAVED_FILTER_PAGE.OPTIMIZER));
  }, []);
  const handleDeletePreset = useCallback(async (name: string) => {
    const result = await deleteWeightPreset(SAVED_FILTER_PAGE.OPTIMIZER, name);
    if (!result.success) throw new Error(result.error || "Failed to delete preset");
    setUserPresets(await listUserWeightPresets(SAVED_FILTER_PAGE.OPTIMIZER));
    if (stateRef.current.preset === name) handleChange({ preset: null }, { immediate: true });
  }, [handleChange]);
  const handleSetDefaultPreset = useCallback(async (name: string | null) => {
    const result = await setDefaultWeightPreset(SAVED_FILTER_PAGE.OPTIMIZER, name);
    if (!result.success) throw new Error(result.error || "Failed to update default preset");
    setUserPresets(await listUserWeightPresets(SAVED_FILTER_PAGE.OPTIMIZER));
  }, []);

  const handleSort = useCallback((column: string, direction: SortDirection) => handleChange({ sort: column, order: direction }), [handleChange]);
  const clearFilters = useCallback(() => {
    setFiltersOpen(false);
    forgetSavedFilters();
    handleChange(buildClearedFilters(stateRef.current), { immediate: true, source: "clear_all" });
  }, [forgetSavedFilters, handleChange]);

  const pill = livePill(status);
  const blocked = blockingPill(pill);
  const bookName = book === null ? "" : sportsbookIdToDisplay(book);
  const leagueName = scope.single === null ? "" : scope.known ? leagueLabel(scope.single) : scope.single.toUpperCase();
  const leaguePrefix = leagueName ? `${leagueName} ` : "";
  const leagueStatus = scope.single === null ? "live" : catalog.value?.leagues.get(scope.single)?.status ?? "live";
  const pricedState = pricedChoices(shownState, diamond);
  const filtersCount = activeFilterCount(pricedState, showWeightsFilter);

  let content: React.ReactNode;
  let fills = false;
  let empty = false;
  if (book === null) {
    empty = true;
    content = (
      <EmptyState
        title={myBooks?.length ? "None of your Books work with Fantasy Props" : `No Fantasy Props books take bets in ${place.state}`}
        description={myBooks?.length ? `None of them take bets in ${place.state}.` : "Turn off Available here only to see every book."}
        action={{ label: "Turn off Available here only", onClick: () => setHereOnly(false) }}
        className="w-full max-w-lg"
      />
    );
  } else if (blocked && view.rows.size === 0) {
    content = <BlockingPanel pill={blocked} />;
  } else if (catalog.value === null) {
    content = <StatePanel variant="loading" shape="page" title="Loading props…" message="Connecting to the live feed." />;
  } else if (catalogLeagues.length === 0) {
    empty = true;
    content = <EmptyState title="No leagues are posting props" description="The live feed has no player props right now." />;
  } else if (!scope.known) {
    content = <StatePanel variant="loading" shape="page" title="Loading props…" message="Picking a league with props." />;
  } else if (view.ended === "unknown_topic") {
    empty = true;
    content = (
      <EmptyState
        title={`${bookName} isn't posting ${leaguePrefix}props`}
        description="Pick another book or league above."
      />
    );
  } else if (view.ended === "forbidden") {
    content = (
      <EmptyState
        title="Fantasy Props needs an active plan"
        description="This session no longer has access to the live props feed. Renew your plan to keep it flowing."
        action={{ label: "See plans", onClick: () => window.location.assign("/pricing?redirect=subscription_required") }}
      />
    );
  } else if (view.ended !== null) {
    content = <BoardStatePanel state={{ kind: "ended", reason: view.ended }} />;
  } else if (!gate.open) {
    content = <StatePanel variant="loading" shape="page" title={`Loading ${bookName} ${leaguePrefix}props…`} message="Connecting to the live feed." />;
  } else if (rows.ids.length === 0 && rows.total === 0) {
    empty = true;
    content = <EmptyState title={`${bookName} has no ${leaguePrefix}props posted`} description="Check back closer to game time or pick another book." />;
  } else if (rows.ids.length === 0) {
    empty = true;
    content = <EmptyState title="No props match your filters" description="Loosen a filter or clear them all." action={{ label: "Clear filters", onClick: clearFilters }} />;
  } else if (cardView) {
    fills = true;
    content = (
      <MobileOptimizerCards
        rows={rows}
        expanded={expanded}
        onToggle={handleToggle}
        tableVariant="props"
        platform={book as SportsbookId}
        columns={columns}
        catalog={catalog.value}
        onShare={setShareRow}
        onChatShare={shareToChat}
        onDismiss={dismissed ? handleHide : undefined}
        slip={cardsSlip}
        showLeague={scope.single === null}
        oddsFormat={format}
      />
    );
  } else {
    fills = true;
    content = (
      <FantasyBoard
        rows={rows}
        events={view.events}
        platform={book}
        columns={columns}
        format={format}
        expanded={expanded}
        sort={{ column: boardState.sort, direction: boardState.order }}
        needPct={needPct}
        slip={boardSlip}
        showLeague={scope.single === null}
        onSort={handleSort}
        onToggle={handleToggle}
        onShare={setShareRow}
        onChatShare={shareToChat}
        onHide={handleHide}
        onOpenHistory={openHistory}
        catalog={catalog.value}
        isMobile={isMobile}
        raceLegs={raceLegs}
        onRace={openRace}
      />
    );
  }

  useLayoutEffect(() => {
    if (fills) gate.markPainted();
  });

  const contentOutcome = liveContentOutcome({
    locked: blocked?.kind === "locked" || view.ended === "forbidden",
    blocked: blocked !== null,
    failed: catalogReady && view.ended !== null && !empty,
    fills,
    empty,
    settled: view.pending === 0,
  });
  const contentReceipt = useMemo(() => liveContentReceipt("fantasy-props", contentOutcome), [contentOutcome]);
  const contentMarker = useToolContentBoundary(contentReceipt, { hasRealRow: contentOutcome === "client-200" });

  const slipPanel = book !== null && slipBook && slip.open ? (
    <FantasySlipPanel
      view={slipView}
      onView={setSlipView}
      best={{
        result: bestSlips,
        rowAt: pool.rowAt,
        events: view.events,
        minEvPct,
        onMinEv: setMinEvPct,
        swappedCount: swappedIds.size,
        onResetSwaps: () => setSwapped({ scope: swapScope, ids: NO_ROWS }),
        entered,
        onUse: useBestSlip,
        onEnter: enterBestSlip,
        onUnenter: removeEntered,
        onSwap: (rowId) => setSwapped((current) => ({ scope: swapScope, ids: new Set([...(current.scope === swapScope ? current.ids : []), rowId]) })),
        onPreview: (rowIds) => setPreview(rowIds === null ? null : new Set(rowIds)),
        modelLabel: state.preset ?? (state.weights !== null ? "your book weights" : "the default fair value"),
        boostPct: state.boost,
        oneLeague,
      }}
      book={book}
      legs={legViews}
      target={slipTarget}
      targets={panelTargets}
      lockedLeague={lock?.league ?? null}
      onTarget={slip.setTarget}
      evaluation={slipEvaluation}
      live={pricing.live}
      grading={grading}
      format={format}
      onRemove={slip.remove}
      onHide={dismissed ? hideLegs : undefined}
      onClear={slip.clear}
      onClose={() => slip.setOpen(false)}
      isMobile={isMobile}
    />
  ) : null;

  return (
    <WhalesGateProvider diamond={diamond} client={liveClient} columnShown={whalesShown} whalesCanPrice={whalesPriceOn(shownState.platform)}>
      <div data-tool-stage="client-mounted" className="odds-black relative flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-hidden p-0 sm:p-4 lg:p-5">
          <section aria-label="Fantasy Props" className="flex h-full min-h-0 flex-col overflow-hidden bg-surface-raised">
            <h1 className="sr-only">Fantasy Props</h1>
            <FantasyBar
              isMobile={isMobile}
              book={book}
              pickedBook={state.platform}
              books={allowedSportsbooks}
              myBooks={myBooks}
              onBook={handleBook}
              selectedLeagues={selectedLeagues}
              leagues={leagues}
              onLeagues={(ids) => handleChange({ leagues: ids.map((id) => id.toUpperCase()) }, { source: "bar" })}
              time={time}
              times={times}
              onTime={(next) => handleChange({ gameDays: [...next.days], startsWithin: next.within }, { source: "bar" })}
              search={state.playerSearch}
              onSearch={(value) => handleChange({ playerSearch: value }, { source: "bar" })}
              liveEnabled={isLive}
              heldAtMs={heldAtMs}
              onToggleLive={toggleLive}
              order={cue}
              filtersCount={filtersCount}
              filtersOpen={filtersOpen}
              onFilters={() => setFiltersOpen((open) => !open)}
              boost={state.boost}
              boostable={boostable}
              onBoost={handleBoost}
              slips={slipBook && !isMobile ? {
                open: slip.open,
                badge: slipsBadge,
                onOpen: () => { if (slip.open) slip.setOpen(false); else openSlips(slip.legs.length > 0 ? "mine" : "best"); },
              } : undefined}
              race={raceView === null ? undefined : { state: raceView, open: raceOpen, onToggle: toggleRace, onClose: closeRace }}
              sort={{ column: state.sort, direction: state.order }}
              onSort={handleSort}
              mobileView={mobileView}
              hiddenCount={dismissed?.count ?? 0}
              onHidden={dismissed?.openManager ?? (() => {})}
              tutorial={tutorial}
            />
            <BookStrip books={listedBooks} leagues={subscribedLeagues} needOf={needOf} weighted={weighted} book={book} heldAtMs={heldAtMs} onPick={(app) => handleBook(app, "book_strip")} />
            {!betLinkNudgeDismissed && <BetLinkNudge dismissed={betLinkNudgeDismissed} />}
            {blocked && view.rows.size > 0 && <div className="flex-shrink-0 px-3 pt-2"><ConnectionBanner pill={blocked} /></div>}
            {!blocked && scope.known && leagueStatus === "warming" && (
              <div className="flex-shrink-0 px-3 pt-2">
                <StatePanel variant="loading" shape="banner" role="status" title={`${leagueName} odds are warming up`} />
              </div>
            )}
            <div className="flex min-h-0 flex-grow">
              <div {...contentMarker} className="relative flex min-h-0 min-w-0 flex-grow flex-col">
                {fills ? content : (
                  <div className="flex min-h-0 flex-grow items-start justify-center overflow-auto p-4">
                    <div className="w-full max-w-md pt-8">{content}</div>
                  </div>
                )}
              </div>
              {!isMobile && slipPanel}
            </div>
            {isMobile && slipBook && (legCount > 0 || bestSlips.best !== null) && (
              <button
                type="button"
                onClick={() => openSlips(slip.legs.length > 0 ? "mine" : "best")}
                aria-label={legCount > 0 ? `Open slip, ${legsText} legs` : `Open +EV Slips, best ${slipsBadge?.text ?? ""}`}
                className="flex h-12 flex-shrink-0 items-center gap-2 border-t border-surface-line bg-surface-raised px-4 text-[13px] font-medium text-foreground"
              >
                {slip.legs.length > 0 ? (
                  <>
                    <span className="data-num rounded-md bg-primary/10 px-1.5 text-[11px] font-semibold leading-5 text-primary">{legsText}</span>
                    <span className="truncate">{slipRoomPct !== null ? `Leg ${legCount + 1} needs ${slipRoomPct.toFixed(1)}%+` : slipTarget !== null && legCount >= slipTarget.legs ? "Slip ready" : "Slip"}</span>
                  </>
                ) : (
                  <>
                    <span>+EV Slips</span>
                    {slipsBadge && <span className="data-num rounded-md bg-accent-value/15 px-1.5 text-[11px] font-semibold leading-5 text-accent-value">{slipsBadge.text}</span>}
                    <span className="data-num text-[12px] text-zinc-500">{readyTypes} ready</span>
                  </>
                )}
                <span className="flex-grow" />
                <span className="text-[12px] text-zinc-400">Open</span>
              </button>
            )}
            {isMobile && slipPanel}
          </section>
        </div>
        {shouldShowActiveFilterChips({ ...pricedState, leagues: [] }, { showHidePushProps: true, todayYmd }) && (
          <div className="hidden shrink-0 px-3 pb-3 sm:px-4 lg:px-5 md:block">
            <ActiveFilterChips state={{ ...shownState, leagues: [] }} onChange={handleChange} onClearAll={forgetSavedFilters} diamond={diamond} />
          </div>
        )}

        <FantasyFiltersSheet
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          state={shownState}
          onChange={handleChange}
          onReset={forgetSavedFilters}
          times={times}
          columns={shownBooks}
          analyticalBookIds={analyticalBookIds}
          showWeightsFilter={showWeightsFilter}
          diamond={diamond}
          ownBook={book}
          picked={state}
          userPresets={userPresets}
          onSavePreset={handleSavePreset}
          onDeletePreset={handleDeletePreset}
          onSetDefault={handleSetDefaultPreset}
          markets={rows.markets}
          matchups={rows.matchups}
          oddsFormat={format}
          onOddsFormatChange={setFormat}
          comparisonOrderRuntime={comparisonOrderRuntime}
          isMobile={isMobile}
          layout={isMobile ? { value: mobileView, onChange: setMobileView } : undefined}
          views={{ toolKey: "optimizer-props", userId: initialUserId }}
        />

        {shareRow !== null && (
          <ShareModal open onOpenChange={(open) => !open && setShareRow(null)} slipData={shareSlipData} />
        )}
        <OddsHistoryModal
          key={historyModal ? getOddsHistorySelectionIdentity(historyModal.selection) : "closed"}
          open={historyModal !== null}
          onOpenChange={(open) => { if (!open) setHistoryModal(null); }}
          selection={historyModal?.selection ?? null}
          fallbackSeries={historyModal?.fallbackSeries ?? null}
          peers={historyModal?.peers ?? null}
          initialView={historyModal?.initialView}
        />
      </div>
    </WhalesGateProvider>
  );
}
